import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, LogOutIcon, CameraIcon } from 'lucide-react';
import { WhatsAppStatusWidget } from '../components/WhatsAppStatusWidget';
import { Toast } from '../components/Toast';
import { TagsInput } from '../components/templates/TagsInput';
import { PaymentQRUpload } from '../components/PaymentQRUpload';
import { ServicesList } from '../components/services/ServicesList';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';

export function Profile() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile, loading, error, updateProfile, refetch } = useProfile();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('Profile updated successfully');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    designation: '',
    organization: '',
    experience: '',
    specializations: [] as string[],
    address: '',
    website: '',
    instagram: '',
    payment_upi_id: '',
    payment_bank_details: ''
  });

  // Sync formData with profile when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        designation: profile.designation || '',
        organization: profile.organization || '',
        experience: profile.experience ? String(profile.experience) : '',
        specializations: profile.specializations || [],
        address: profile.address || '',
        website: profile.website || '',
        instagram: profile.instagram || '',
        payment_upi_id: profile.payment_upi_id || '',
        payment_bank_details: profile.payment_bank_details || ''
      });
    }
  }, [profile]);

  const handleSave = async () => {
    const success = await updateProfile({
      name: formData.name,
      phone: formData.phone,
      designation: formData.designation,
      organization: formData.organization,
      experience: formData.experience ? parseInt(formData.experience) : null,
      specializations: formData.specializations,
      address: formData.address,
      website: formData.website,
      instagram: formData.instagram,
      payment_upi_id: formData.payment_upi_id || null,
      payment_bank_details: formData.payment_bank_details || null,
    });

    if (success) {
      setIsEditing(false);
      setToastMessage('Profile updated successfully');
      setShowToast(true);
    } else {
      setToastMessage('Failed to update profile');
      setShowToast(true);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // Clean up localStorage
      localStorage.removeItem('whatsapp_connected');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-6">
        <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeftIcon className="w-6 h-6 text-[#212121]" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">My Profile</h1>
          </div>
        </div>
        <div className="p-4 flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] pb-6">
        <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ArrowLeftIcon className="w-6 h-6 text-[#212121]" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">My Profile</h1>
          </div>
        </div>
        <div className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-600 font-medium">Error loading profile</p>
            <p className="text-red-500 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return <div className="min-h-screen bg-[#F5F5F5] pb-6">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeftIcon className="w-6 h-6 text-[#212121]" />
          </button>
          <h1 className="text-xl font-semibold text-[#212121]">My Profile</h1>
        </div>
      </div>
      <div className="p-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center">
                  <span className="text-3xl">👩‍⚕️</span>
                </div>
                <button className="absolute bottom-0 right-0 w-7 h-7 bg-[#5B8E87] rounded-full flex items-center justify-center shadow-md">
                  <CameraIcon className="w-4 h-4 text-white" />
                </button>
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#212121]">
                  {formData.name}
                </h2>
                <p className="text-sm text-gray-600">{formData.designation}</p>
              </div>
            </div>
            {!isEditing ? <button onClick={() => setIsEditing(true)} className="text-[#5B8E87] font-medium text-sm">
                Edit
              </button> : <button onClick={handleSave} className="bg-[#5B8E87] text-white px-4 py-2 rounded-lg font-medium text-sm">
                Save
              </button>}
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <h3 className="font-semibold text-base text-[#212121] mb-4">
            Basic Info
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Full Name
              </label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Email
              </label>
              <input type="email" value={formData.email} disabled className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-gray-50 text-gray-600" />
              <p className="text-xs text-gray-500 mt-1">
                Login email cannot be changed
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Phone
              </label>
              <input type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <h3 className="font-semibold text-base text-[#212121] mb-4">
            Professional
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Designation
              </label>
              <input type="text" value={formData.designation} onChange={e => setFormData({
              ...formData,
              designation: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Clinic / Organization
              </label>
              <input type="text" value={formData.organization} onChange={e => setFormData({
              ...formData,
              organization: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Years of Experience
              </label>
              <input type="number" value={formData.experience} onChange={e => setFormData({
              ...formData,
              experience: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Specializations
              </label>
              {isEditing ? (
                <div className="mt-1">
                  <TagsInput
                    value={formData.specializations}
                    onChange={(tags) => setFormData({ ...formData, specializations: tags })}
                  />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {formData.specializations.length > 0 ? (
                    formData.specializations.map((spec, index) => (
                      <span key={index} className="px-3 py-1 bg-[#5B8E87] bg-opacity-10 text-[#5B8E87] rounded-full text-sm font-medium">
                        {spec}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">No specializations added yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <h3 className="font-semibold text-base text-[#212121] mb-4">
            Contact & Branding
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Clinic Address
              </label>
              <textarea value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} disabled={!isEditing} rows={3} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Website
              </label>
              <input type="text" value={formData.website} onChange={e => setFormData({
              ...formData,
              website: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Instagram
              </label>
              <input type="text" value={formData.instagram} onChange={e => setFormData({
              ...formData,
              instagram: e.target.value
            })} disabled={!isEditing} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50" />
            </div>
          </div>
        </div>

        {/* Payment Details Section */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <h3 className="font-semibold text-base text-[#212121] mb-4">
            Payment Details
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            These details will be included in payment reminders based on the payment method selected
          </p>

          <div className="space-y-4">
            {/* UPI ID field */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                UPI ID
              </label>
              <input
                type="text"
                value={formData.payment_upi_id || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_upi_id: e.target.value
                })}
                disabled={!isEditing}
                placeholder="e.g., 9876543210@paytm"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shared when payment method is UPI
              </p>
            </div>

            {/* UPI QR Code upload */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                UPI QR Code
              </label>
              <PaymentQRUpload
                currentQRPath={profile?.payment_qr_code_url || null}
                onUploadSuccess={() => {
                  // Refetch profile to get updated QR path
                  refetch();
                }}
                onDeleteSuccess={() => {
                  // Refetch profile after deletion
                  refetch();
                }}
                disabled={!isEditing}
              />
            </div>

            {/* Bank Account Details textarea */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Bank Account Details
              </label>
              <textarea
                value={formData.payment_bank_details || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_bank_details: e.target.value
                })}
                disabled={!isEditing}
                rows={5}
                placeholder={'e.g.,\nAccount Name: Dr. Sarah Clinic\nAccount Number: 1234567890\nIFSC: ICIC0001234\nBank: ICICI Bank'}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all resize-none disabled:bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shared when payment method is Bank Transfer
              </p>
            </div>

            {/* Security notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
              <span className="text-blue-600 text-lg">🔒</span>
              <div>
                <p className="text-sm font-medium text-blue-900">Secure & Private</p>
                <p className="text-xs text-blue-700 mt-1">
                  Your payment details are encrypted and only visible to you.
                  They are only shared when you send payment reminders to clients.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* My Services Section */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <h3 className="font-semibold text-base text-[#212121] mb-2">
            My Services
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Define your services for quick billing. These will appear when creating detailed payments.
          </p>
          <ServicesList />
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
          <h3 className="font-semibold text-base text-[#212121] mb-4">
            System
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-3">
                WhatsApp Connection
              </label>
              <WhatsAppStatusWidget />
            </div>
          </div>
        </div>
        <button onClick={() => setShowLogoutModal(true)} className="w-full bg-red-50 text-red-600 font-semibold py-4 rounded-xl hover:bg-red-100 transition-colors flex items-center justify-center gap-2">
          <LogOutIcon className="w-5 h-5" />
          Logout
        </button>
      </div>
      {showLogoutModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden animate-scale-in">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-[#212121] mb-2">
                Confirm Logout
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to logout?
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowLogoutModal(false)} className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
                <button onClick={handleLogout} className="flex-1 bg-red-500 text-white font-semibold py-3 rounded-xl hover:bg-red-600 transition-colors">
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>}
      {showToast && <Toast message={toastMessage} onClose={() => setShowToast(false)} />}
    </div>;
}